import { useState } from 'react';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { GpcDisplayValue, useResolvedGpc } from '@/components/GpcDisplay';
import { ATTRIBUT_FELDER, istErgaenzt, type AttributFeld } from '@/lib/changeSummary';
import { OPTIMIERT_KEY } from './ChangeSummary';
import type { ValidationWarning } from '../ValidationNotice';
import type { WorkbenchProduct, WorkbenchEditState } from '@/hooks/useWorkbench';

/**
 * Die Felder, deren Validierungshinweis AN EINEM CHIP landet.
 *
 * Alles andere bleibt im Hinweisband ueber der Liste. Spec 4.3 sagt woertlich
 * "nur die, die zu keinem einzelnen Feld gehoeren" - waere das buchstaeblich
 * umgesetzt, verschwaenden `gtin_invalid`, `title_too_long` und
 * `image_too_small` spurlos, denn zu diesen Feldern gibt es keinen Chip. Ein
 * Hinweis, der auf einer Freigabeflaeche ersatzlos verschwindet, ist der
 * teuerste denkbare Ausgang. Der Zweck von Abschnitt 7 ist, die DOPPELUNG zu
 * beenden (der fest verdrahtete GTIN-Kasten neben dem Hinweisband), nicht die
 * Menge zu kuerzen.
 */
export const CHIP_FIELDS: ReadonlySet<string> = new Set<string>(ATTRIBUT_FELDER);

type ChipState = 'enriched' | 'unchanged' | 'missing';

const STATE_CLASS: Record<ChipState, string> = {
  enriched: 'border-primary/40 bg-primary/5',
  unchanged: 'border-border bg-transparent text-muted-foreground',
  // Tonale Variante statt deckender Signalfarbe: das rohe warning-Token
  // faellt mit weisser Schrift durch AA (2.13:1 hell).
  missing: 'border-warning/40 bg-warning-light/50 text-warning-ink',
};

const STATE_DOT: Record<ChipState, string | null> = {
  enriched: 'bg-primary',
  unchanged: null,
  missing: 'bg-warning',
};

/**
 * Der Zustand steht sonst NUR in der Farbe des Punktes. Farbe allein ist keine
 * Information (WCAG 1.4.1), und am Screenreader waere jeder Chip ununterscheidbar
 * ein "Farbe Navy". Der Satz wird vorgelesen und nicht angezeigt.
 */
const STATE_LABEL: Record<ChipState, string> = {
  enriched: 'chips.enriched',
  unchanged: 'chips.unchanged',
  missing: 'chips.missing',
};

interface AttributeChipsProps {
  product: WorkbenchProduct;
  editState: WorkbenchEditState;
  onEditChange: (field: keyof WorkbenchEditState, value: string | string[]) => void;
  warnings: ValidationWarning[] | null | undefined;
}

/**
 * Die Attribute als Chips.
 *
 * Statt sieben Zeilen mit Label, Original und Wert steht hier eine Zeile
 * Chips, und der Zustand traegt die Aussage: Petrol heisst "die KI hat das
 * getan", die blosse Umrandung heisst "war schon da", gelb heisst "eine Regel
 * verlangt das und es fehlt". Ein leeres Attribut, das keine Regel benennt,
 * erscheint gar nicht - sieben Chips, von denen vier nichts sagen, kosten
 * Aufmerksamkeit an der Stelle, an der sie knapp ist.
 */
export function AttributeChips({
  product,
  editState,
  onEditChange,
  warnings,
}: AttributeChipsProps) {
  const { t, i18n } = useTranslation('workbench');
  const { t: tOpt } = useTranslation('optimization');
  const [editing, setEditing] = useState<AttributFeld | null>(null);
  const { chipMarke, kategorieZeile } = useEntwurf();

  const label = (feld: AttributFeld) => tOpt(`progressModal.phase3.attributes.${feld}`);

  /**
   * Dieselbe Regel wie im Hinweisband: uebersetzt wird ueber die stabile,
   * sprachneutrale `rule_id`, nicht ueber die `message` aus der Datenbank -
   * die ist hartkodiertes Englisch aus `_shared/product-validation.ts`. Ohne
   * das stand am Chip ein englischer Satz in einer deutschen Oberflaeche,
   * sichtbar im Tooltip und hoerbar am Screenreader.
   *
   * Rueckfall auf die Rohmeldung, damit eine neue Backend-Regel hier nicht zu
   * einer leeren Warnung fuehrt: Englisch ist schlechter als Deutsch und weit
   * besser als nichts.
   */
  const warnungsText = (w: ValidationWarning): string => {
    const schluessel = w.rule_id ? `validation.rules.${w.rule_id}` : null;
    return schluessel && i18n.exists(`workbench:${schluessel}`) ? t(schluessel) : w.message;
  };

  // Erster Hinweis je Feld gewinnt; mehrere zu demselben Feld sind heute
  // nicht vorgesehen.
  const warningByField = new Map<string, ValidationWarning>();
  for (const w of warnings ?? []) {
    if (w.field && !warningByField.has(w.field)) warningByField.set(w.field, w);
  }

  const chips = ATTRIBUT_FELDER.map((feld) => {
    const original = (product[feld] as string | null) ?? null;
    const optimiert = (editState[OPTIMIERT_KEY[feld]] as string | undefined) ?? null;
    const wert = (optimiert ?? '').trim() || (original ?? '').trim();
    const warning = warningByField.get(feld) ?? null;

    let state: ChipState;
    if (wert === '') {
      // Ohne benennende Regel gibt es nichts zu sagen: Chip entfaellt.
      if (!warning) return null;
      state = 'missing';
    } else {
      state = istErgaenzt({ feld, original, optimiert }) ? 'enriched' : 'unchanged';
    }
    return { feld, wert, state, warning };
  }).filter((c): c is NonNullable<typeof c> => c !== null);

  /* Kategorie als Schlusszeile (Entwurfs-Schalter): nur ein GESETZTER Wert
     wandert aus dem Raster in die Zeile - ein fehlender bleibt als Warn-Chip,
     eine leere Schlusszeile warnt niemanden. Die Aufloesung (useResolvedGpc
     und damit useLanguage/useAuth) lebt in der Zeilen-Komponente selbst. */
  const gpcChip = chips.find((c) => c.feld === 'google_product_category');
  const gpcAlsZeile =
    kategorieZeile === 'zeile' && gpcChip && gpcChip.state !== 'missing' ? gpcChip : null;
  /* Dritter Wert (T5): bleibt IM Chip-Raster, nur die Renderform des
     GPC-Elements weicht ab (siehe die Schleife unten) - chipListe verliert
     das Feld deshalb NICHT wie bei 'zeile'. */
  const gpcAlsGestrichelt =
    kategorieZeile === 'gestrichelterChip' && gpcChip && gpcChip.state !== 'missing'
      ? gpcChip
      : null;
  const chipListe = gpcAlsZeile
    ? chips.filter((c) => c.feld !== 'google_product_category')
    : chips;

  if (chips.length === 0) {
    return (
      <section className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
          {t('chips.heading')}
        </h4>
        <p className="text-xs text-muted-foreground">{t('chips.empty')}</p>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
        {t('chips.heading')}
      </h4>
      <div className="flex flex-wrap gap-1.5">
        {chipListe.map(({ feld, wert, state, warning }) => {
          const isGpc = feld === 'google_product_category';

          if (editing === feld) {
            return (
              <div
                key={feld}
                className="flex items-center gap-1.5 rounded-md border border-primary px-2 py-1"
              >
                <span className="text-[10px] uppercase tracking-wider text-foreground/70">
                  {label(feld)}
                </span>
                <Input
                  autoFocus
                  data-testid={`chip-input-${feld}`}
                  value={(editState[OPTIMIERT_KEY[feld]] as string) ?? ''}
                  onChange={(e) => onEditChange(OPTIMIERT_KEY[feld], e.target.value)}
                  onBlur={() => setEditing(null)}
                  placeholder={t('chips.placeholder')}
                  className="h-6 w-32 py-0 text-base md:text-xs"
                />
              </div>
            );
          }

          if (isGpc && gpcAlsGestrichelt) {
            return (
              <KategorieChipGestrichelt
                key={feld}
                wert={gpcAlsGestrichelt.wert}
                state={gpcAlsGestrichelt.state as 'enriched' | 'unchanged'}
                chipMarke={chipMarke}
                labelText={label('google_product_category')}
                editText={t('actions.editField')}
                stateLabelText={t(STATE_LABEL[gpcAlsGestrichelt.state])}
                warnung={gpcAlsGestrichelt.warning ? warnungsText(gpcAlsGestrichelt.warning) : null}
                onEdit={() => setEditing('google_product_category')}
              />
            );
          }

          return (
            <span key={feld} className="inline-flex items-center">
              <button
                type="button"
                data-testid={`chip-${feld}`}
                data-chip-state={state}
                onClick={() => setEditing(feld)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors hover:border-primary ${STATE_CLASS[state]}`}
              >
                {chipMarke === 'sparkle' && state === 'enriched' ? (
                  /* Dasselbe Zeichen wie in der Aenderungsliste: Petrol-
                     Sparkle heisst ueberall "das kam von der KI". Nur am
                     ergaenzten Chip - `missing` behaelt seinen Warn-Punkt,
                     Merchant-Werte bleiben ohne Marke. Der Zustand steht
                     weiterhin zusaetzlich im vorgelesenen STATE_LABEL. */
                  <Sparkles aria-hidden="true" className="h-3 w-3 flex-shrink-0 text-primary" />
                ) : (
                  STATE_DOT[state] && (
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 rounded-full ${STATE_DOT[state]}`}
                    />
                  )
                )}
                {/* Weder `opacity-70` noch `text-muted-foreground`: der Token liegt im
                    hellen Thema bei 4.45:1 auf Karte, und 70 Prozent Deckkraft
                    darauf ergaben gemessene 2.7:1 - der Feldname sagt, WELCHES
                    Attribut man ansieht, und er ist nirgends sonst zu lesen.
                    `text-foreground/70` haengt an keinem knappen Token. */}
                <span className="text-[10px] uppercase tracking-wider text-foreground/70">
                  {label(feld)}
                </span>
                <span className="sr-only">{t(STATE_LABEL[state])}</span>
                <span className={state === 'enriched' ? 'font-semibold' : ''}>
                  {wert === '' ? (
                    t('chips.missing')
                  ) : isGpc ? (
                    <GpcDisplayValue value={wert} />
                  ) : (
                    wert
                  )}
                </span>
                {/* Die Handlung ans ENDE, als Teil des Inhalts. Vorher trug
                    der Knopf ein `aria-label` - und das ersetzt den Namen aus
                    dem Inhalt vollstaendig (accname: "name from author" vor
                    "name from content"). Angesagt wurde damit nur
                    "Anpassen: Farbe"; Wert und Zustand, also der ganze Grund
                    fuer den Chip, waren fuer Screenreader unerreichbar. */}
                <span className="sr-only">{t('actions.editField')}</span>
              </button>
              {warning && (
                <span
                  data-testid={`chip-warning-${feld}`}
                  title={warnungsText(warning)}
                  className="ml-1 inline-flex items-center text-warning-ink"
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                  <span className="sr-only">{warnungsText(warning)}</span>
                </span>
              )}
            </span>
          );
        })}
      </div>
      {gpcAlsZeile &&
        (editing === 'google_product_category' ? (
          <div className="flex items-center gap-1.5 rounded-md border border-primary px-2 py-1">
            <span className="flex-shrink-0 text-[10px] uppercase tracking-wider text-foreground/70">
              {label('google_product_category')}
            </span>
            <Input
              autoFocus
              data-testid="chip-input-google_product_category"
              value={(editState[OPTIMIERT_KEY.google_product_category] as string) ?? ''}
              onChange={(e) => onEditChange(OPTIMIERT_KEY.google_product_category, e.target.value)}
              onBlur={() => setEditing(null)}
              placeholder={t('chips.placeholder')}
              className="h-6 w-full py-0 text-base md:text-xs"
            />
          </div>
        ) : (
          <KategorieZeileAnzeige
            wert={gpcAlsZeile.wert}
            enriched={gpcAlsZeile.state === 'enriched'}
            labelText={label('google_product_category')}
            editText={t('actions.editField')}
            warnung={gpcAlsZeile.warning ? warnungsText(gpcAlsZeile.warning) : null}
            onEdit={() => setEditing('google_product_category')}
          />
        ))}
    </section>
  );
}

/**
 * Vorn gekuerzter Kategorie-Pfad: bei mehr als drei Segmenten stehen nur die
 * letzten drei, davor eine Ellipse - das ENDE des Pfads traegt die
 * Information. Das letzte Segment ist fett. Der volle Pfad haengt als
 * `title` am Knopf.
 */
function KategoriePfad({ text }: { text: string }) {
  const segmente = text
    .split('>')
    .map((s) => s.trim())
    .filter(Boolean);
  const gezeigt = segmente.length > 3 ? segmente.slice(-3) : segmente;
  return (
    <>
      {segmente.length > 3 && <span aria-hidden="true">{'… › '}</span>}
      {gezeigt.map((s, i) =>
        i === gezeigt.length - 1 ? (
          <b key={i}>{s}</b>
        ) : (
          <span key={i}>
            {s}
            {/* Trenner nur visuell - vorgelesen wuerde er zwischen jedem
                Segment stoeren (Review-NIT 24.08.). */}
            <span aria-hidden="true">{' › '}</span>
          </span>
        ),
      )}
    </>
  );
}

/**
 * Die gestrichelte Kategorie-CHIP-Variante (Entwurfs-Schalter `kategorieZeile`,
 * dritter Wert). Anders als `KategorieZeileAnzeige` bleibt sie INNERHALB des
 * Chip-Rasters statt eine eigene Zeile darunter zu bilden: sichtbar ist nur
 * das letzte Pfadsegment, der volle Pfad steht im `title` UND im `aria-label`
 * des Knopfes - Farbe und Kuerzung allein waeren fuer einen Screenreader
 * keine Information. `aria-label` ersetzt hier bewusst die sonst uebliche
 * "name from content"-Regel (siehe die Begruendung am generischen Chip weiter
 * oben): der sichtbare Text ist absichtlich gekuerzt, die zugaengliche
 * Bezeichnung darf es nicht sein. Klick oeffnet dasselbe Eingabefeld wie
 * jeder andere Chip.
 *
 * Eigene Komponente aus demselben Grund wie `KategorieZeileAnzeige`:
 * `useResolvedGpc` ist ein Hook und darf nicht in der `chips.map`-Schleife
 * aufgerufen werden.
 */
function KategorieChipGestrichelt({
  wert,
  state,
  chipMarke,
  labelText,
  editText,
  stateLabelText,
  warnung,
  onEdit,
}: {
  wert: string;
  state: 'enriched' | 'unchanged';
  chipMarke: 'punkt' | 'sparkle';
  labelText: string;
  editText: string;
  stateLabelText: string;
  warnung: string | null;
  onEdit: () => void;
}) {
  const resolved = useResolvedGpc(wert);
  const text = resolved.text ?? wert;
  const segmente = text
    .split('>')
    .map((s) => s.trim())
    .filter(Boolean);
  const letztes = segmente[segmente.length - 1] || text;

  return (
    <span className="inline-flex items-center">
      <button
        type="button"
        data-testid="chip-google_product_category"
        data-chip-state={state}
        onClick={onEdit}
        title={text}
        aria-label={`${labelText}: ${text}. ${stateLabelText}. ${editText}`}
        className={`inline-flex items-center gap-1.5 rounded-md border border-dashed px-2 py-1 text-xs transition-colors hover:border-primary ${STATE_CLASS[state]}`}
      >
        {chipMarke === 'sparkle' && state === 'enriched' ? (
          <Sparkles aria-hidden="true" className="h-3 w-3 flex-shrink-0 text-primary" />
        ) : (
          STATE_DOT[state] && (
            <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${STATE_DOT[state]}`} />
          )
        )}
        <span className="text-[10px] uppercase tracking-wider text-foreground/70">
          {labelText}
        </span>
        <span className={state === 'enriched' ? 'font-semibold' : ''}>{letztes}</span>
      </button>
      {warnung && (
        <span
          data-testid="chip-warning-google_product_category"
          title={warnung}
          className="ml-1 inline-flex items-center text-warning-ink"
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          <span className="sr-only">{warnung}</span>
        </span>
      )}
    </span>
  );
}

/**
 * Die leise Kategorie-Schlusszeile. Eigene Komponente, damit `useResolvedGpc`
 * (und dahinter useLanguage/useAuth) NUR laeuft, wenn der Schalter sie
 * wirklich mountet - der STANDARD-Pfad von /workbench sieht keinen neuen
 * Hook.
 */
function KategorieZeileAnzeige({
  wert,
  enriched,
  labelText,
  editText,
  warnung,
  onEdit,
}: {
  wert: string;
  enriched: boolean;
  labelText: string;
  editText: string;
  warnung: string | null;
  onEdit: () => void;
}) {
  const resolved = useResolvedGpc(wert);
  const text = resolved.text ?? wert;
  return (
    <div
      data-testid="kategorie-zeile"
      className="flex min-w-0 items-center gap-2 border-t border-border/60 pt-2"
    >
      {enriched && (
        /* Sparkle nur bei KI-Wert - Icon-Sprache: Provenienz, kein Schmuck. */
        <Sparkles aria-hidden="true" className="h-3 w-3 flex-shrink-0 text-primary" />
      )}
      <span className="flex-shrink-0 text-[10px] uppercase tracking-wider text-foreground/70">
        {labelText}
      </span>
      <button
        type="button"
        data-testid="kategorie-zeile-pfad"
        onClick={onEdit}
        title={text}
        /* `relative` ist die URSACHE-Behebung des horizontalen Seiten-Scrolls
           bei 1024 px (W6, gemessen am 08.09.2026 gegen die Produktion:
           `documentElement.scrollWidth` 1052 gegen `clientWidth` 1024, und
           `body.scrollWidth` glatte 1024 - der Ueberhang lag also allein am
           Wurzelelement).
           Mechanik: `.sr-only` ist `position: absolute`. Ohne positionierten
           Elternteil ist sein umschliessender Block der initiale, und ein
           absolut positioniertes Element wird NUR von Vorfahren geklippt, die
           in seiner Kette umschliessender Bloecke liegen. Weder dieser Knopf
           (`truncate`, also `overflow: hidden`) noch das `overflow-x-hidden`
           des `<main>` sind das, also entkam der Knoten beiden. Seine
           statische Position sitzt hinter dem UNGEKUERZTEN, nicht umbrechenden
           Pfadtext - gemessen bei x = 1051, und genau diese 28 px hat die
           Seite gescrollt.
           `relative` macht den Knopf zum umschliessenden Block; sein
           `overflow: hidden` greift damit auch auf diesen Knoten. Bewusst
           NICHT `overflow-x: hidden` weiter oben: das haette den Scrollbalken
           versteckt und die Ursache stehen lassen. */
        className="relative min-w-0 flex-1 truncate text-left text-xs transition-colors hover:text-primary"
      >
        <KategoriePfad text={text} />
        <span className="sr-only"> {editText}</span>
      </button>
      {resolved.id && (
        <span className="flex-shrink-0 font-mono text-[10px] text-muted-foreground">
          ID {resolved.id}
        </span>
      )}
      {warnung && (
        <span
          data-testid="chip-warning-google_product_category"
          title={warnung}
          className="inline-flex items-center text-warning-ink"
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          <span className="sr-only">{warnung}</span>
        </span>
      )}
    </div>
  );
}
